/**
 * Save Preset Modal Component
 *
 * Modal dialog for saving the current filter configuration as a preset.
 * Features:
 * - Input field for preset name
 * - Validation (non-empty, max length)
 * - Duplicate name detection with overwrite confirmation
 * - Keyboard support (Enter to save, Escape to cancel)
 *
 * Styled for Monutrack dark theme with Monumenta Green (#D2FA52).
 */

import { useState, useEffect, useRef } from 'react';
import { X, AlertCircle } from 'lucide-react';
import type { FilterPresetFilters } from '../services/filterPresetService';

// ============================================================================
// CONSTANTS
// ============================================================================

const MAX_NAME_LENGTH = 50;

// ============================================================================
// TYPES
// ============================================================================

export interface SavePresetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  currentFilters: FilterPresetFilters;
  /** Check if preset name already exists */
  checkNameExists: (name: string) => boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function SavePresetModal({
  isOpen,
  onClose,
  onSave,
  currentFilters,
  checkNameExists,
}: SavePresetModalProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setError(null);
      setShowOverwriteConfirm(false);
      // Focus input after short delay to allow modal animation
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Handle keyboard events
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (showOverwriteConfirm) {
          handleConfirmOverwrite();
        } else {
          handleSave();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, name, showOverwriteConfirm]);

  const handleClose = () => {
    setName('');
    setError(null);
    setShowOverwriteConfirm(false);
    onClose();
  };

  const validateName = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) {
      return 'O nome do preset é obrigatório';
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      return `O nome deve ter no máximo ${MAX_NAME_LENGTH} caracteres`;
    }
    return null;
  };

  const handleSave = () => {
    const validationError = validateName(name);
    if (validationError) {
      setError(validationError);
      return;
    }

    const trimmedName = name.trim();

    // Check for duplicate name
    if (checkNameExists(trimmedName)) {
      setShowOverwriteConfirm(true);
      return;
    }

    onSave(trimmedName);
    handleClose();
  };

  const handleConfirmOverwrite = () => {
    const trimmedName = name.trim();
    onSave(trimmedName);
    handleClose();
  };

  const handleCancelOverwrite = () => {
    setShowOverwriteConfirm(false);
    inputRef.current?.focus();
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setError(null);
    setShowOverwriteConfirm(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-md bg-[#24262B] rounded-xl shadow-2xl overflow-hidden border border-[#3A3D44]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#3A3D44]">
          <h2 className="text-lg font-semibold text-white">Salvar Período</h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 hover:bg-[#3A3D44] rounded-lg transition-colors text-[#838998] hover:text-white"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Preview of current filters */}
          <div className="mb-4 p-3 bg-[#2A2D34] rounded-lg border border-[#3A3D44]">
            <p className="text-xs text-[#838998] uppercase tracking-wide mb-2">
              Configuração a salvar
            </p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-[#838998]">Período:</span>
                <span className="text-white ml-2">
                  {currentFilters.startDate} → {currentFilters.endDate}
                </span>
              </div>
              <div>
                <span className="text-[#838998]">Agrupamento:</span>
                <span className="text-white ml-2 capitalize">
                  {currentFilters.granularity}
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-[#838998]">Comparação:</span>
                <span className="text-white ml-2">
                  {currentFilters.comparisonMode === 'none'
                    ? 'Nenhuma'
                    : currentFilters.comparisonMode === 'previous_period'
                      ? 'Período Anterior'
                      : 'Mesmo Período Ano Anterior'}
                </span>
              </div>
            </div>
          </div>

          {/* Name input */}
          <div className="mb-4">
            <label
              htmlFor="preset-name"
              className="block text-sm font-medium text-[#838998] mb-2"
            >
              Nome do preset
            </label>
            <input
              ref={inputRef}
              id="preset-name"
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="Ex: Análise Q1 2025"
              maxLength={MAX_NAME_LENGTH}
              className={`
                w-full px-4 py-2 bg-[#2A2D34] border rounded-lg text-white text-sm
                placeholder:text-[#5A5D66]
                focus:outline-none focus:ring-2 focus:ring-[#D2FA52] focus:border-transparent
                ${error ? 'border-red-500' : 'border-[#3A3D44]'}
              `}
            />
            {error && (
              <div className="flex items-center gap-1 mt-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}
            <p className="text-xs text-[#5A5D66] mt-1 text-right">
              {name.length}/{MAX_NAME_LENGTH}
            </p>
          </div>

          {/* Overwrite confirmation */}
          {showOverwriteConfirm && (
            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-sm text-yellow-400">
                Já existe um preset com este nome. Deseja substituí-lo?
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={handleConfirmOverwrite}
                  className="px-3 py-1.5 bg-yellow-500 text-black text-sm font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
                >
                  Substituir
                </button>
                <button
                  type="button"
                  onClick={handleCancelOverwrite}
                  className="px-3 py-1.5 bg-[#3A3D44] text-white text-sm font-medium rounded-lg hover:bg-[#4A4D54] transition-colors"
                >
                  Renomear
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#3A3D44]">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-[#838998] hover:text-white hover:bg-[#3A3D44] rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={showOverwriteConfirm}
            className={`
              px-4 py-2 text-sm font-semibold rounded-lg transition-colors
              focus:outline-none focus:ring-2 focus:ring-[#D2FA52] focus:ring-offset-1 focus:ring-offset-[#24262B]
              ${
                showOverwriteConfirm
                  ? 'bg-[#3A3D44] text-[#838998] cursor-not-allowed'
                  : 'bg-[#D2FA52] text-[#232429] hover:bg-[#c4eb44] cursor-pointer'
              }
            `}
          >
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

export default SavePresetModal;
